import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './CategoryListing.css';

type Product = {
    id: string;
    title: string;
    price: number;
    images: string[];
    categoryId: string;
    availableNum?: string; // 新增
    variants?: { image?: string; colorId?: string; colorCode?: string; stock?: number }[]; // Add colorId, colorCode, and stock
};

type Category = {
    id: string;
    name: string;
    parentId: string;
};

const PAGE_SIZE_OPTIONS = [12, 18, 36];

const CategoryListing: React.FC = () => {
    const { categoryId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [category, setCategory] = useState<Category | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [pageSize, setPageSize] = useState(Number(searchParams.get('size')) || 12);
    const [sortType, setSortType] = useState<'name_asc' | 'name_desc' | 'price_asc' | 'price_desc'>('name_asc');
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
    const [categoryPath, setCategoryPath] = useState<Category[]>([]);
    const [, setHoveredIdx] = useState<number | null>(null);
    const [hoveredImgIdx, setHoveredImgIdx] = useState<{ [key: number]: number }>({});

    const navigate = useNavigate();

    // 获取分类信息（URL 中的 categoryId 即 Firestore 文档 ID）
    useEffect(() => {
        if (!categoryId) return;
        const fetchCategory = async () => {
            try {
                const cached = localStorage.getItem(`category_${categoryId}`);
                const cacheTime = localStorage.getItem(`category_${categoryId}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 10 * 60 * 1000;

                if (cached && cacheAge < maxAge) {
                    try {
                        setCategory(JSON.parse(cached));
                        return;
                    } catch { }
                }

                const docSnap = await getDoc(doc(db, 'categories', categoryId));
                if (docSnap.exists()) {
                    const categoryData = { id: docSnap.id, ...docSnap.data() } as Category;
                    setCategory(categoryData);
                    try {
                        localStorage.setItem(`category_${categoryId}`, JSON.stringify(categoryData));
                        localStorage.setItem(`category_${categoryId}_time`, Date.now().toString());
                    } catch { /* Safari 隐私模式可能无法写入 */ }
                }
            } catch (err) {
                console.error('Failed to fetch category:', err);
            }
        };
        fetchCategory();
    }, [categoryId]);

    // 获取该分类下所有商品
    useEffect(() => {
        if (!categoryId) {
            setLoading(false);
            return;
        }
        const fetchProducts = async () => {
            setLoading(true);
            setLoadError(null);

            const cacheKey = `category_products_${categoryId}_${sortType}`;
            try {
                const cached = localStorage.getItem(cacheKey);
                const cacheTime = localStorage.getItem(`${cacheKey}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 2 * 60 * 1000;

                if (cached && cacheAge < maxAge) {
                    try {
                        const parsed = JSON.parse(cached);
                        if (Array.isArray(parsed)) {
                            setAllProducts(parsed);
                            return;
                        }
                    } catch { }
                }

                let categoryIds = [categoryId];
                const childIds = await getAllChildCategoryIds(categoryId);
                if (childIds.length > 0) {
                    categoryIds = [categoryId, ...childIds];
                }

                let allProducts: Product[] = [];
                for (let i = 0; i < categoryIds.length; i += 10) {
                    const batchIds = categoryIds.slice(i, i + 10);
                    if (batchIds.length === 0) continue;

                    let orderField: 'title' | 'price' = 'title';
                    let orderDirection: 'asc' | 'desc' = 'asc';
                    if (sortType === 'name_asc') {
                        orderField = 'title'; orderDirection = 'asc';
                    } else if (sortType === 'name_desc') {
                        orderField = 'title'; orderDirection = 'desc';
                    } else if (sortType === 'price_asc') {
                        orderField = 'price'; orderDirection = 'asc';
                    } else if (sortType === 'price_desc') {
                        orderField = 'price'; orderDirection = 'desc';
                    }

                    const q = query(
                        collection(db, 'products'),
                        where('category', 'array-contains-any', batchIds),
                        where('visible', '==', true),
                        orderBy(orderField, orderDirection)
                    );
                    const snapshot = await getDocs(q);
                    snapshot.forEach(docSnap => {
                        const data = docSnap.data();
                        allProducts.push({
                            id: docSnap.id,
                            title: data.title,
                            price: data.price,
                            images: data.images || [],
                            categoryId: data.categoryId,
                            availableNum: data.availableNum || '',
                            variants: data.variants || [],
                        });
                    });
                }
                setAllProducts(allProducts);
                try {
                    localStorage.setItem(cacheKey, JSON.stringify(allProducts));
                    localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
                } catch { /* Safari 隐私模式 */ }
            } catch (err) {
                console.error('Failed to fetch category products:', err);
                setLoadError('Failed to load products. Please refresh the page.');
                setAllProducts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
        // eslint-disable-next-line
    }, [categoryId, sortType]);

    // 分页
    useEffect(() => {
        const startIdx = (currentPage - 1) * pageSize;
        setProducts(allProducts.slice(startIdx, startIdx + pageSize));
    }, [allProducts, currentPage, pageSize]);

    // 更新URL参数
    useEffect(() => {
        setSearchParams({
            size: String(pageSize),
            sort: sortType,
            page: String(currentPage),
        });
    }, [pageSize, sortType, currentPage, setSearchParams]);

    // 获取分类路径
    useEffect(() => {
        const fetchCategoryPath = async () => {
            if (categoryId) {
                const path = await getCategoryPath(categoryId);
                setCategoryPath(path);
                if (path.length) setCategory(path[path.length - 1]);
            }
        };
        fetchCategoryPath();
    }, [categoryId]);

    // Breadcrumb
    const breadcrumb = (
        <nav className="breadcrumb category-listing-breadcrumb">
            <Link to="/">Home</Link>
            {categoryPath.map((cat, idx) => (
                <span key={cat.id}>
                    {' '} &gt;{' '}
                    {idx === categoryPath.length - 1 ? (
                        <span>{cat.name}</span>
                    ) : (
                        <Link to={`/category/${cat.id}`}>{cat.name}</Link>
                    )}
                </span>
            ))}
        </nav>
    );

    // 分页按钮
    const totalPages = Math.ceil(allProducts.length / pageSize);

    return (
        <>
            <div className="category-listing-container">
                <div className="category-listing-header">
                    <h1 style={{ fontWeight: 700, fontSize: '2rem' }}>
                        {category?.name || 'Category'}
                    </h1>
                    {breadcrumb}
                    <div className="category-listing-results-count">
                        Showing {allProducts.length} results
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 50 }}>
                    <div>
                        Show:{' '}
                        <select
                            value={pageSize}
                            onChange={e => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            style={{ marginRight: 16 }}
                        >
                            {PAGE_SIZE_OPTIONS.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        per page
                    </div>
                    <div>
                        Sort by:{' '}
                        <select
                            value={sortType}
                            onChange={e => {
                                setSortType(e.target.value as any);
                                setCurrentPage(1);
                            }}
                            style={{ marginRight: 8 }}
                        >
                            <option value="name_asc">Name: A-Z</option>
                            <option value="name_desc">Name: Z-A</option>
                            <option value="price_asc">Price: Low to High</option>
                            <option value="price_desc">Price: High to Low</option>
                        </select>
                    </div>
                </div>
                {loadError && (
                    <div style={{ color: '#c0392b', marginBottom: 16 }}>{loadError}</div>
                )}
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <div className="product-grid">
                        {products.map((product, idx) => {
                            // 优先用 variants 里的图片
                            const variantImages = product.variants?.map((v: any) => v.image).filter(Boolean) || [];
                            const allImages = [...variantImages, ...(product.images || [])];
                            const hasSecondImg = allImages.length > 1;
                            const showImgIdx = hoveredImgIdx[idx] ?? 0;

                            return (
                                <div
                                    className="product-card"
                                    key={product.id}
                                    onClick={() => navigate(`/product/${product.id}`, { state: { categoryId: product.categoryId, categoryName: category?.name } })}
                                    style={{
                                        cursor: 'pointer',
                                        height: 300,
                                        padding: 16,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start'
                                    }}
                                    onMouseEnter={() => {
                                        if (hasSecondImg) {
                                            // 延迟切换到第二张
                                            setTimeout(() => {
                                                setHoveredImgIdx(prev => ({ ...prev, [idx]: 1 }));
                                            }, 200); // 200ms后切换
                                        }
                                        setHoveredIdx(idx);
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredImgIdx(prev => ({ ...prev, [idx]: 0 }));
                                        setHoveredIdx(null);
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 180,
                                            height: 180,
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            margin: '0 auto 8px auto',
                                            background: '#f5f5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        {/* 第一张图片 */}
                                        <img
                                            loading="lazy"
                                            src={allImages[0] || 'https://via.placeholder.com/150'}
                                            alt={product.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                opacity: showImgIdx === 0 ? 1 : 0,
                                                transition: 'opacity 0.5s cubic-bezier(.4,0,.2,1)'
                                            }}
                                        />
                                        {/* 第二张图片（如果有） */}
                                        {allImages[1] && (
                                            <img
                                                src={allImages[1]}
                                                alt={product.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    opacity: showImgIdx === 1 ? 1 : 0,
                                                    transition: 'opacity 0.5s cubic-bezier(.4,0,.2,1)'
                                                }}
                                            />
                                        )}
                                    </div>
                                    <p style={{ fontWeight: 500, margin: '8px 0 4px 0', color: '#222' }}>{product.title}</p>
                                    {product.variants && product.variants.length > 0 && (
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '4px 0 8px 0' }}>
                                            {product.variants.map((variant, vIdx) => (
                                                <span
                                                    key={variant.colorId || vIdx}
                                                    title={variant.colorCode}
                                                    style={{
                                                        display: 'inline-block',
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '50%',
                                                        background: variant.colorCode,
                                                        border: '1.5px solid #eee',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    <p style={{ color: '#27ae60', fontWeight: 600 }}>
                                        ${product.price}
                                        {category?.name !== 'Used Furniture' && '/month'}
                                    </p>
                                    {category?.name === 'Used Furniture' ? null : (
                                        product.variants && product.variants.length > 0 ? (
                                            product.variants.some(v => typeof v.stock === 'number' && v.stock > 0) ? (
                                                <p style={{ color: '#27ae60', fontSize: 14 }}>Available</p>
                                            ) : (
                                                <p style={{ color: '#e74c3c', fontSize: 14 }}>Out of Stock</p>
                                            )
                                        ) : (
                                            (!product.availableNum || Number(product.availableNum) === 0) ? (
                                                <p style={{ color: '#e74c3c', fontSize: 14 }}>Out of Stock</p>
                                            ) : (
                                                <p style={{ color: '#888', fontSize: 14 }}>{product.availableNum} Available</p>
                                            )
                                        )
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {/* 分页 */}
                <div style={{ marginTop: 24, textAlign: 'center' }}>
                    {(() => {
                        const maxVisiblePages = 8;
                        let startPage = 1;
                        let endPage = totalPages;

                        if (totalPages > maxVisiblePages) {
                            if (currentPage <= 4) {
                                endPage = maxVisiblePages;
                            } else if (currentPage >= totalPages - 3) {
                                startPage = totalPages - maxVisiblePages + 1;
                            } else {
                                startPage = currentPage - 3;
                                endPage = currentPage + 4;
                            }
                        }

                        const pages = [];

                        // 添加第一页和省略号
                        if (startPage > 1) {
                            pages.push(
                                <button
                                    key={1}
                                    onClick={() => setCurrentPage(1)}
                                    style={{
                                        margin: '0 4px',
                                        padding: '6px 14px',
                                        borderRadius: 4,
                                        border: '1px solid #ccc',
                                        background: '#fff',
                                        color: '#222',
                                        cursor: 'pointer',
                                        fontWeight: 400,
                                    }}
                                >
                                    1
                                </button>
                            );
                            if (startPage > 2) {
                                pages.push(
                                    <span key="ellipsis1" style={{ margin: '0 4px', color: '#666' }}>
                                        ...
                                    </span>
                                );
                            }
                        }

                        // 添加可见页码
                        for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i)}
                                    style={{
                                        margin: '0 4px',
                                        padding: '6px 14px',
                                        borderRadius: 4,
                                        border: i === currentPage ? '2px solid #27ae60' : '1px solid #ccc',
                                        background: i === currentPage ? '#eafaf1' : '#fff',
                                        color: '#222',
                                        cursor: 'pointer',
                                        fontWeight: i === currentPage ? 700 : 400,
                                    }}
                                >
                                    {i}
                                </button>
                            );
                        }

                        // 添加最后一页和省略号
                        if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                                pages.push(
                                    <span key="ellipsis2" style={{ margin: '0 4px', color: '#666' }}>
                                        ...
                                    </span>
                                );
                            }
                            pages.push(
                                <button
                                    key={totalPages}
                                    onClick={() => setCurrentPage(totalPages)}
                                    style={{
                                        margin: '0 4px',
                                        padding: '6px 14px',
                                        borderRadius: 4,
                                        border: '1px solid #ccc',
                                        background: '#fff',
                                        color: '#222',
                                        cursor: 'pointer',
                                        fontWeight: 400,
                                    }}
                                >
                                    {totalPages}
                                </button>
                            );
                        }

                        return pages;
                    })()}
                </div>
            </div>
        </>
    );
};

const getAllChildCategoryIds = async (parentId: string): Promise<string[]> => {
    // 1. 先从 localStorage 读取
    const cached = localStorage.getItem(`child_categories_${parentId}`);
    const cacheTime = localStorage.getItem(`child_categories_${parentId}_time`);
    const now = Date.now();
    const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
    const maxAge = 10 * 60 * 1000; // 10分钟缓存

    if (cached && cacheAge < maxAge) {
        try {
            return JSON.parse(cached);
        } catch { }
    }

    // 2. 再请求 Firestore
    const subCatSnap = await getDocs(query(collection(db, 'categories'), where('parentId', '==', parentId)));
    const childIds = subCatSnap.docs.map(doc => doc.id);
    let allIds = [...childIds];
    for (const id of childIds) {
        const subIds = await getAllChildCategoryIds(id);
        allIds = allIds.concat(subIds);
    }

    // 3. 缓存子分类ID
    localStorage.setItem(`child_categories_${parentId}`, JSON.stringify(allIds));
    localStorage.setItem(`child_categories_${parentId}_time`, Date.now().toString());

    return allIds;
};

const getCategoryPath = async (categoryId: string): Promise<Category[]> => {
    // 1. 先从 localStorage 读取
    const cached = localStorage.getItem(`category_path_${categoryId}`);
    const cacheTime = localStorage.getItem(`category_path_${categoryId}_time`);
    const now = Date.now();
    const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
    const maxAge = 10 * 60 * 1000; // 10分钟缓存

    if (cached && cacheAge < maxAge) {
        try {
            return JSON.parse(cached);
        } catch { }
    }

    // 2. 再请求 Firestore
    const path: Category[] = [];
    let currentId = categoryId;
    while (currentId) {
        const docRef = doc(db, 'categories', currentId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const cat = { id: docSnap.id, ...docSnap.data() } as Category;
            path.unshift(cat);
            const parentId = cat.parentId;
            if (!parentId || parentId === 'null') break;
            currentId = parentId;
        } else {
            break;
        }
    }

    // 3. 缓存分类路径
    localStorage.setItem(`category_path_${categoryId}`, JSON.stringify(path));
    localStorage.setItem(`category_path_${categoryId}_time`, Date.now().toString());

    return path;
};

export default CategoryListing;